import { Box, Grid } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import UniversalToolBarWithDialogs from "../../components/bar/UniversalToolBarWithDialogs";
import { SnackbarError } from "../../components/base/SnackbarAlert/SnackbarError";
import { SnackbarSuccess } from "../../components/base/SnackbarAlert/SnackbarSuccess";
import { TextFilter } from "../../components/common/TextFilter";
import FilterTable from "../../components/table/FilterTable";
import DefaultPageWrapper from "../../components/wrapper/DefaultPageWrapper";
import LoaderWrapper from "../../components/wrapper/LoaderWrapper";
import { LANGUAGE_KEY_IN_LOCAL_STORAGE } from "../../helpers/constants";
import { useDialogBetter } from "../../hooks/useDialogBetter";
import useLocalStorage from "../../hooks/useLocalStorage";
import usePaginationWithSearchParams from "../../hooks/usePaginationWithSearchParams";
import { useLeads, useLeadsStatuses, useRemoveLeadMutation } from "../../services/leadsService";
import TableService from "../../services/tableService";
import { LeadAddDialog } from "./LeadAddDialog";
import { LeadEditDialog } from "./LeadEditDialog";
import { LeadImportListDialog } from "./LeadImportListDialog";
import { LEADS_TABLE_NAME, useLeadsTableFieldsConfig } from "./LeadsPageTableConfig";

export function LeadsPage(props) {
    const { pageName } = props;

    const LEADS_TABLE_FIELDS_CONFIG = useLeadsTableFieldsConfig();

    const TABLE_CONFIGS = [{ name: LEADS_TABLE_NAME, config: LEADS_TABLE_FIELDS_CONFIG }];

    const { t } = useTranslation();
    const [hasEditPermission] = [true];

    const [dataLocal, setDataLocal] = useState([]);
    const [recordsCount, setRecordsCount] = useState(0);
    const [selectedRowId, setSelectedRowId] = useState();
    const [hiddenColumnsForTables, setHiddenColumnsForTables] = useState([]);
    const [columnsOrdersForTables, setColumnsOrdersForTables] = useState([]);
    const [checkedAll, setCheckedAll] = useState(false);

    const { openDialog: openAddLeadDialogBetter } = useDialogBetter();
    const { openDialog: openEditLeadDialogBetter } = useDialogBetter();
    const { openDialog: openImportListDialogBetter } = useDialogBetter();

    const {
        resetPageNumber,
        searchParams,
        setSearchParams,
        page: dataPage,
        pageSize: dataPageSize,
        handleChangePageWithSearchParams: handleChangeDataPage,
        handleChangePageSizeWithSearchParams: handleChangeDataPageSize,
    } = usePaginationWithSearchParams();

    const isFooTableLoading = false; // setLoading when data are preparing

    const leadDataExportHeaders = useMemo(() => {
        return TableService.getTableExportHeaders(
            LEADS_TABLE_FIELDS_CONFIG, // @ts-ignore
            hiddenColumnsForTables?.[LEADS_TABLE_NAME], // @ts-ignore
            columnsOrdersForTables?.[LEADS_TABLE_NAME],
        );
    }, [
        // @ts-ignore
        hiddenColumnsForTables?.[LEADS_TABLE_NAME],
        // @ts-ignore
        columnsOrdersForTables?.[LEADS_TABLE_NAME],
        LEADS_TABLE_FIELDS_CONFIG,
    ]);

    const { data: leadsRows, refetch, isLoading } = useLeads(searchParams);
    useEffect(() => {
        if (leadsRows?.results) {
            setDataLocal(leadsRows.results);
            setRecordsCount(leadsRows.count);
        }
    }, [leadsRows, dataPage, dataPageSize]);

    const data = useMemo(
        () =>
            TableService.getPreparedDataForCollapsibleTable(
                LEADS_TABLE_FIELDS_CONFIG,
                dataLocal, // @ts-ignore
                hiddenColumnsForTables?.[LEADS_TABLE_NAME]
                    ? // @ts-ignore
                      hiddenColumnsForTables?.[LEADS_TABLE_NAME]
                    : [], // @ts-ignore
                // onClickButton
                columnsOrdersForTables?.[LEADS_TABLE_NAME]
                    ? // @ts-ignore
                      columnsOrdersForTables?.[LEADS_TABLE_NAME]
                    : [],
            ),
        [
            LEADS_TABLE_FIELDS_CONFIG,
            dataLocal,
            hiddenColumnsForTables,
            columnsOrdersForTables,
            // onClickButton
        ],
    );

    const [language, _] = useLocalStorage(LANGUAGE_KEY_IN_LOCAL_STORAGE, "pl");

    const { data: leadsStatusesData } = useLeadsStatuses();

    const getFilteringData = useCallback(() => {
        return {
            statuses: leadsStatusesData,
            language: language,
        };
    }, [leadsStatusesData, language]);

    useEffect(() => {
        refetch();
        console.log("params changed to", searchParams.toString());
    }, [searchParams]);

    useEffect(() => {
        if (selectedRowId) {
            const selectedRow = dataLocal.find((row) => row.id === selectedRowId);
            if (!selectedRow) {
                return;
            }
            console.log("selectedRow", selectedRow);
            openEditLeadDialogBetter({
                Component: LeadEditDialog,
                componentProps: { leadId: selectedRowId, lead: selectedRow },
                onCloseFn: () => {
                    setSelectedRowId(undefined);
                },
            });
        }
    }, [selectedRowId]);

    const { mutate: removeLead } = useRemoveLeadMutation({
        onSuccess: () => {
            toast.custom(
                (curToast) => <SnackbarSuccess message={t("lead.removedSuccessfully")} />,
                {
                    position: "bottom-right",
                },
            );
        },
        onError: (error) => {
            toast.custom((curToast) => <SnackbarError message={t("lead.removeError")} />, {
                position: "bottom-right",
            });
        },
    });

    const onClickRemoveLead = useCallback(() => {
        console.log("dataLocal rem", dataLocal);
        dataLocal // @ts-ignore
            .filter((row) => row.selected)
            .forEach((row) => {
                removeLead(row.id);
            });
    }, [dataLocal, removeLead]);

    const hasSelectedRows = useMemo(() => {
        // @ts-ignore
        return dataLocal.some((row) => row.selected);
    }, [dataLocal]);

    return (
        // @ts-ignore
        <DefaultPageWrapper tagTitle={t("headers.leads")}>
            <Grid
                item
                container
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
                rowSpacing={1}
            >
                <Grid item xs={12}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "start",
                            width: "40%",
                            marginLeft: "50px",
                        }}
                    >
                        <TextFilter
                            filterKey={"person"}
                            searchParams={searchParams}
                            setSearchParams={setSearchParams}
                            withPagination
                            resetPageNumber={resetPageNumber}
                            placeholder={
                                t("clients.filter.masterFilterPersonPlaceholder") ?? undefined
                            }
                        />
                    </Box>
                </Grid>
                <Grid item xs={12} marginY={1}>
                    <UniversalToolBarWithDialogs
                        pageName={pageName}
                        tableConfig={TABLE_CONFIGS}
                        filteringData={getFilteringData()}
                        refreshTable={refetch}
                        // @ts-ignore
                        resetPageNumber={resetPageNumber}
                        hiddenColumnsForTables={hiddenColumnsForTables}
                        setHiddenColumnsForTables={setHiddenColumnsForTables}
                        columnsOrdersForTables={columnsOrdersForTables}
                        setColumnsOrdersForTables={setColumnsOrdersForTables}
                        showMassActionButton={false}
                        showOpenCloseFilterButton={false}
                        showCleanfiltersButton={true}
                        showMyFiltersButton={true}
                        showSearchButton={true}
                        itemType={"lead"}
                        style={{ marginBlock: "5px" }}
                        onClickCreateItem={() =>
                            openAddLeadDialogBetter({
                                Component: LeadAddDialog,
                                componentProps: {},
                            })
                        }
                        // onClickCreateItem={() => { }}
                        showCreateButton={hasEditPermission}
                        mdGridButton={hasEditPermission ? 1.25 : 2.75}
                        showRemoveLeadButton={true}
                        disableRemoveLeadButton={!hasSelectedRows}
                        onClickRemoveLead={onClickRemoveLead}
                        showImportList={true}
                        onClickImportList={() => {
                            openImportListDialogBetter({
                                Component: LeadImportListDialog,
                                componentProps: {
                                    title: t("lead.LeadImportListDialog.title"),
                                },
                                onCloseFn: () => {
                                    refetch();
                                },
                            });
                        }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <LoaderWrapper showLoader={isFooTableLoading}>
                        <FilterTable
                            // @ts-ignore
                            isLoading={isLoading}
                            data={data}
                            countRecords={recordsCount}
                            resetPageNumber={resetPageNumber}
                            selectedItemId={selectedRowId}
                            style={{ maxHeight: "60vh" }}
                            headersConfig={TableService.orderColumns(
                                TableService.hideHiddenColumns(
                                    LEADS_TABLE_FIELDS_CONFIG, // @ts-ignore
                                    hiddenColumnsForTables?.[LEADS_TABLE_NAME]
                                        ? // @ts-ignore
                                          hiddenColumnsForTables?.[LEADS_TABLE_NAME]
                                        : [],
                                ), // @ts-ignore
                                columnsOrdersForTables?.[LEADS_TABLE_NAME]
                                    ? // @ts-ignore
                                      columnsOrdersForTables?.[LEADS_TABLE_NAME]
                                    : [],
                            )}
                            showCleanFilterIcon={true}
                            showContextMenu={false}
                            showExportToFileButton={true}
                            exportToFileUrl={"sales/leads/export/"}
                            exportToFileSearchParams={searchParams}
                            exportToFileHeaders={leadDataExportHeaders}
                            exportToFileFileName={`${t("lead.exportFileName")}.xlsx`}
                            showDetailsIcon={false}
                            showCheckbox={true}
                            checkedAll={checkedAll}
                            page={dataPage}
                            onPageChange={handleChangeDataPage}
                            disableRemoveLeadButton={!hasSelectedRows}
                            rowsPerPage={dataPageSize}
                            onRowsPerPageChange={handleChangeDataPageSize}
                            setDataLocal={setDataLocal}
                            setCheckedAll={setCheckedAll}
                            setSelectedRowId={setSelectedRowId}
                        />
                    </LoaderWrapper>
                </Grid>
            </Grid>
        </DefaultPageWrapper>
    );
}
